/**
 * Applies Regios customer-segment discounts to product cards on non-PDP pages
 * (collection, home page featured collections, search, predictive search, etc.).
 *
 * Relies on the Regios DOPP API (window.RegiosDOPP.api.v0) which is injected
 * globally by the "Discounts Embed" theme app extension.
 *
 * Required card attributes (set in jkl-card.liquid):
 *   data-regios-dopp-generic-product-id  — numeric product ID
 *   data-variant-id                      — numeric variant ID
 *   [data-price]                         — price in decimal (e.g. "21.9")
 *   [data-compare-at-price]              — compare-at price in decimal (or "0")
 *
 * Required price element classes (set in jkl-pdp-price.liquid):
 *   .regios-dopp-generic-price-container
 *   .regios-dopp-generic-price-item--sale
 *   .regios-dopp-generic-price-item--regular
 *   .regios-dopp-generic-badge  (optional)
 *
 * Re-runs on dynamically injected cards via the jkl:products-loaded event
 * (dispatched by jkl-search-drawer.js after predictive search injection, and
 * by collection grids after Load More).
 */

(function () {
  'use strict';

  let cachedApi = null;

  async function applyDiscountsToCards(api) {
    const cards = document.querySelectorAll('[data-regios-dopp-generic-product-id]:not([data-regios-plp-done])');
    if (!cards.length) return;

    const tasks = Array.from(cards).map(async (card) => {
      card.dataset.regiosPlpDone = '1';

      const productId = parseInt(card.dataset.regiosDoppGenericProductId, 10);
      const variantId = parseInt(card.dataset.variantId, 10);
      if (!productId || !variantId) return;

      const priceContainer = card.querySelector('.regios-dopp-generic-price-container');
      const saleEl = card.querySelector('.regios-dopp-generic-price-item--sale');
      const regularEl = card.querySelector('.regios-dopp-generic-price-item--regular');
      if (!priceContainer || !saleEl || !regularEl) return;

      const linkEl = card.querySelector('[data-price]');
      if (!linkEl) return;

      const regularPriceInCents = Math.round(parseFloat(linkEl.dataset.price) * 100);
      const compareAtRaw = parseFloat(linkEl.dataset.compareAtPrice || 0);
      const compareAtPriceInCents = compareAtRaw > 0
        ? Math.round(compareAtRaw * 100)
        : undefined;

      try {
        const result = await api.calculateDiscountedPrices({
          productId,
          variantId,
          regularPriceInCents,
          compareAtPriceInCents,
        });

        if (result.salePrice.amount < result.regularPrice.amount) {
          saleEl.textContent = result.salePrice.formatted;
          regularEl.textContent = result.regularPrice.formatted;
          regularEl.classList.remove('hidden');
          priceContainer.classList.add('jkl-pdp-price--on-sale');

          const pct = Math.round((1 - result.salePrice.amount / result.regularPrice.amount) * 100);

          // Image-overlay badge (only present when no native compare_at_price)
          const cardBadge = card.querySelector('[data-regios-card-badge]');
          if (cardBadge) {
            cardBadge.textContent = `-${pct}%`;
            cardBadge.classList.remove('hidden');
          }

          // Regios description
          const descEl = card.querySelector('.regios-dopp-description');
          if (descEl && result.description?.html) {
            descEl.innerHTML = result.description.html;
            if (result.description.css) descEl.style.cssText = result.description.css;
            descEl.classList.remove('hidden');
          }
        }
      } catch {
        // Product not eligible or API unavailable — keep original price
      }
    });

    await Promise.allSettled(tasks);
  }

  function init() {
    if (window.RegiosDOPP?.api?.v0) {
      cachedApi = window.RegiosDOPP.api.v0;
      applyDiscountsToCards(cachedApi);
    } else {
      window.addEventListener('regios-dopp:api-initialized', (e) => {
        cachedApi = e.detail.api;
        applyDiscountsToCards(cachedApi);
      });
    }

    document.addEventListener('jkl:products-loaded', () => {
      if (cachedApi) applyDiscountsToCards(cachedApi);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
